// @ts-nocheck
import { useState, useMemo } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Modal,
  TextInput,
  Textarea,
  Select,
  NumberInput,
  Badge,
  ActionIcon,
  Tabs,
  Loader,
  Center,
  Rating,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { DateTimePicker } from '@mantine/dates';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconCalendar,
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconList,
  IconCalendarEvent,
} from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable, type Column } from '../../components/DataTable';
import {
  useInterviews,
  useScheduleInterview,
  useCancelInterview,
  useSubmitInterviewFeedback,
} from '../../hooks/useRecruitment';
import type {
  Interview,
  InterviewType,
  ScheduleInterviewInput,
  InterviewFeedbackInput,
} from '../../types/recruitment';

const INTERVIEW_TYPE_OPTIONS: { value: InterviewType; label: string }[] = [
  { value: 'PHONE', label: 'Phone' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'IN_PERSON', label: 'In Person' },
  { value: 'PANEL', label: 'Panel' },
  { value: 'TECHNICAL', label: 'Technical' },
];

const TYPE_COLORS: Record<string, string> = {
  PHONE: 'blue',
  VIDEO: 'cyan',
  IN_PERSON: 'green',
  PANEL: 'indigo',
  TECHNICAL: 'violet',
};

export default function InterviewSchedulePage() {
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null);

  const [scheduleForm, setScheduleForm] = useState({
    applicantId: '',
    interviewerId: '',
    scheduledAt: null as string | null,
    duration: 60,
    type: 'VIDEO' as InterviewType,
    location: '',
  });

  const [feedbackForm, setFeedbackForm] = useState({
    rating: 0,
    feedback: '',
  });

  const { data: interviewsData, isLoading } = useInterviews();
  const interviews = (interviewsData as any)?.interviews as Interview[] | undefined;
  const scheduleMutation = useScheduleInterview();
  const cancelMutation = useCancelInterview();
  const feedbackMutation = useSubmitInterviewFeedback();

  // ── Group by date for calendar view ─────────────────────────────────
  const groupedByDate = useMemo(() => {
    if (!interviews) return new Map<string, Interview[]>();
    const grouped = new Map<string, Interview[]>();
    const sorted = [...interviews].sort(
      (a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime(),
    );
    for (const interview of sorted) {
      const dateKey = new Date(interview.scheduledAt).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      const existing = grouped.get(dateKey) || [];
      existing.push(interview);
      grouped.set(dateKey, existing);
    }
    return grouped;
  }, [interviews]);

  const handleSchedule = async () => {
    if (!scheduleForm.applicantId || !scheduleForm.interviewerId || !scheduleForm.scheduledAt) return;
    const input: ScheduleInterviewInput = {
      applicantId: scheduleForm.applicantId,
      interviewerId: scheduleForm.interviewerId,
      scheduledAt: scheduleForm.scheduledAt ?? '',
      duration: scheduleForm.duration,
      type: scheduleForm.type,
      location: scheduleForm.location || undefined,
    };

    try {
      await scheduleMutation.mutateAsync(input);
      notifications.show({ title: 'Scheduled', message: 'Interview scheduled', color: 'green' });
      setScheduleModalOpen(false);
      setScheduleForm({
        applicantId: '',
        interviewerId: '',
        scheduledAt: null,
        duration: 60,
        type: 'VIDEO',
        location: '',
      });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to schedule interview', color: 'red' });
    }
  };

  const handleCancel = async (interview: Interview) => {
    try {
      await cancelMutation.mutateAsync({ id: interview.id });
      notifications.show({ title: 'Cancelled', message: 'Interview cancelled', color: 'gray' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to cancel interview', color: 'red' });
    }
  };

  const handleNoShow = async (interview: Interview) => {
    const input: InterviewFeedbackInput = {
      id: interview.id,
      rating: 0,
      feedback: 'Candidate did not attend (No-Show)',
    };
    try {
      await feedbackMutation.mutateAsync(input);
      notifications.show({ title: 'Marked', message: 'Interview marked as No-Show', color: 'orange' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to update interview', color: 'red' });
    }
  };

  const openFeedbackModal = (interview: Interview) => {
    setSelectedInterview(interview);
    setFeedbackForm({ rating: 0, feedback: '' });
    setFeedbackModalOpen(true);
  };

  const handleSubmitFeedback = async () => {
    if (!selectedInterview || feedbackForm.rating === 0) return;
    const input: InterviewFeedbackInput = {
      id: selectedInterview.id,
      rating: feedbackForm.rating,
      feedback: feedbackForm.feedback,
    };

    try {
      await feedbackMutation.mutateAsync(input);
      notifications.show({ title: 'Submitted', message: 'Feedback submitted', color: 'green' });
      setFeedbackModalOpen(false);
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to submit feedback', color: 'red' });
    }
  };

  // ── Table Columns ─────────────────────────────────────────────────
  const columns: Column<Interview>[] = [
    {
      key: 'applicantName',
      label: 'Applicant',
      sortable: true,
    },
    {
      key: 'interviewerName',
      label: 'Interviewer',
      sortable: true,
    },
    {
      key: 'scheduledAt',
      label: 'Date / Time',
      sortable: true,
      render: (row) => new Date(row.scheduledAt).toLocaleString(),
    },
    {
      key: 'duration',
      label: 'Duration',
      width: 90,
      render: (row) => `${row.duration} min`,
    },
    {
      key: 'type',
      label: 'Type',
      width: 110,
      render: (row) => (
        <Badge color={TYPE_COLORS[row.type] || 'gray'} variant="light" size="sm">
          {row.type.replace(/_/g, ' ')}
        </Badge>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: 110,
      render: (row) => <StatusBadge status={row.status} />,
    },
  ];

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Interview Schedule"
        subtitle={interviews ? `${interviews.length} interviews` : undefined}
        actions={
          <Group gap="sm">
            <Tabs
              value={viewMode}
              onChange={(v) => setViewMode((v as 'list' | 'calendar') || 'list')}
              variant="pills"
            >
              <Tabs.List>
                <Tabs.Tab value="list" icon={<IconList size={14} />}>
                  List
                </Tabs.Tab>
                <Tabs.Tab value="calendar" icon={<IconCalendarEvent size={14} />}>
                  Calendar
                </Tabs.Tab>
              </Tabs.List>
            </Tabs>
            <Button
              icon={<IconCalendar size={16} />}
              onClick={() => setScheduleModalOpen(true)}
            >
              Schedule Interview
            </Button>
          </Group>
        }
      />

      {/* List View */}
      {viewMode === 'list' && (
        <DataTable<Record<string, unknown>>
          columns={columns as unknown as import('../../components/DataTable').Column<Record<string, unknown>>[]}
          data={(interviews ?? []) as unknown as Record<string, unknown>[]}
          loading={isLoading}
          getRowKey={(row) => row.id as string}
          emptyMessage="No interviews scheduled"
          actions={(row) => {
            const interview = row as unknown as Interview;
            return (
            <Group gap={4} wrap="nowrap">
              {interview.status === 'SCHEDULED' && (
                <>
                  <ActionIcon
                    variant="subtle"
                    color="green"
                    size="sm"
                    title="Complete (Add Feedback)"
                    onClick={() => openFeedbackModal(interview)}
                  >
                    <IconCheck size={14} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    title="Cancel"
                    onClick={() => handleCancel(interview)}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    color="orange"
                    size="sm"
                    title="No-Show"
                    onClick={() => handleNoShow(interview)}
                  >
                    <IconAlertTriangle size={14} />
                  </ActionIcon>
                </>
              )}
            </Group>
          ); }}
        />
      )}

      {/* Calendar View (grouped by date) */}
      {viewMode === 'calendar' && (
        <Stack gap="md">
          {groupedByDate.size === 0 ? (
            <Paper withBorder p="xl" ta="center">
              <Text c="dimmed" size="sm">
                No interviews scheduled
              </Text>
            </Paper>
          ) : (
            Array.from(groupedByDate.entries()).map(([dateLabel, dayInterviews]) => (
              <Paper key={dateLabel} withBorder p="md" radius="md">
                <Text fw={600} size="lg" mb="sm">
                  {dateLabel}
                </Text>
                <Stack gap="xs">
                  {dayInterviews.map((interview) => (
                    <Paper key={interview.id} withBorder p="sm" radius="sm" bg="gray.0">
                      <Group justify="space-between" ta="flex-start">
                        <Stack gap={2}>
                          <Group gap="sm">
                            <Text fw={600} size="sm">
                              {new Date(interview.scheduledAt).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </Text>
                            <Text size="sm" c="dimmed">
                              ({interview.duration} min)
                            </Text>
                            <Badge
                              color={TYPE_COLORS[interview.type] || 'gray'}
                              variant="light"
                              size="xs"
                            >
                              {interview.type.replace(/_/g, ' ')}
                            </Badge>
                            <StatusBadge status={interview.status} size="xs" />
                          </Group>
                          <Text size="sm">
                            <Text span fw={500}>
                              {interview.applicantName}
                            </Text>
                            {' with '}
                            {interview.interviewerName}
                          </Text>
                          {interview.location && (
                            <Text size="xs" c="dimmed">
                              {interview.location}
                            </Text>
                          )}
                        </Stack>
                        {interview.status === 'SCHEDULED' && (
                          <Group gap={4}>
                            <ActionIcon
                              variant="subtle"
                              color="green"
                              size="sm"
                              title="Complete"
                              onClick={() => openFeedbackModal(interview)}
                            >
                              <IconCheck size={14} />
                            </ActionIcon>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              size="sm"
                              title="Cancel"
                              onClick={() => handleCancel(interview)}
                            >
                              <IconX size={14} />
                            </ActionIcon>
                          </Group>
                        )}
                        {interview.status === 'COMPLETED' && interview.rating != null && (
                          <Rating value={interview.rating} readOnly size="xs" />
                        )}
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              </Paper>
            ))
          )}
        </Stack>
      )}

      {/* Schedule Interview Modal */}
      <Modal
        opened={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        title="Schedule Interview"
        size="md"
      >
        <Stack gap="sm">
          <TextInput
            label="Applicant ID"
            required
            value={scheduleForm.applicantId}
            onChange={(e) =>
              setScheduleForm((f) => ({ ...f, applicantId: e.currentTarget.value }))
            }
            placeholder="Applicant ID"
          />

          <TextInput
            label="Interviewer ID"
            required
            value={scheduleForm.interviewerId}
            onChange={(e) =>
              setScheduleForm((f) => ({ ...f, interviewerId: e.currentTarget.value }))
            }
            placeholder="Interviewer employee ID"
          />

          <DateTimePicker
            label="Date & Time"
            required
            value={scheduleForm.scheduledAt}
            onChange={(v) => setScheduleForm((f) => ({ ...f, scheduledAt: v }))}
            placeholder="Select date and time"
          />

          <Group grow>
            <NumberInput
              label="Duration (minutes)"
              value={scheduleForm.duration}
              onChange={(v) =>
                setScheduleForm((f) => ({ ...f, duration: typeof v === 'number' ? v : 60 }))
              }
              min={15}
              max={480}
              step={15}
            />
            <Select
              label="Type"
              required
              value={scheduleForm.type}
              onChange={(v) =>
                setScheduleForm((f) => ({
                  ...f,
                  type: (v as InterviewType) || 'VIDEO',
                }))
              }
              data={INTERVIEW_TYPE_OPTIONS}
            />
          </Group>

          <TextInput
            label="Location"
            value={scheduleForm.location}
            onChange={(e) =>
              setScheduleForm((f) => ({ ...f, location: e.currentTarget.value }))
            }
            placeholder="Room / Zoom link / Address"
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setScheduleModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSchedule}
              loading={scheduleMutation.isPending}
              disabled={
                !scheduleForm.applicantId ||
                !scheduleForm.interviewerId ||
                !scheduleForm.scheduledAt
              }
            >
              Schedule
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Feedback Modal */}
      <Modal
        opened={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        title="Complete Interview"
        size="md"
      >
        {selectedInterview && (
          <Stack gap="sm">
            <Text size="sm">
              Interview with <Text span fw={600}>{selectedInterview.applicantName}</Text>
            </Text>

            <div>
              <Text fw={500} size="sm" mb={4}>
                Rating
              </Text>
              <Rating
                value={feedbackForm.rating}
                onChange={(v) => setFeedbackForm((f) => ({ ...f, rating: v }))}
                size="lg"
              />
            </div>

            <Textarea
              label="Feedback"
              minRows={4}
              value={feedbackForm.feedback}
              onChange={(e) =>
                setFeedbackForm((f) => ({ ...f, feedback: e.currentTarget.value }))
              }
              placeholder="Interview notes and feedback..."
            />

            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={() => setFeedbackModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmitFeedback}
                loading={feedbackMutation.isPending}
                disabled={feedbackForm.rating === 0}
              >
                Submit Feedback
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
